import type { Prototype } from '@atlas/api-contract';
import { Link } from '@tanstack/react-router';
import { MessageSquare, MoreHorizontal, Pencil, Trash2, Upload } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { resourceOwnerId, useCan } from '@/features/auth';
import { formatAbsoluteTime, formatRelativeTime } from '@/lib/format/date';

export interface PrototypeCardProps {
  prototype: Prototype;
  onEdit: (prototype: Prototype) => void;
  onDelete: (prototype: Prototype) => void;
  onUpload: (prototype: Prototype) => void;
}

/**
 * One prototype on a project page (spec R6.6).
 *
 * Built like `ProjectCard`: the title is the only anchor and stretches over the
 * whole card with `after:inset-0`, which keeps a menu button from being nested
 * inside a link and leaves the keyboard two stops per card.
 *
 * Two judgements are encoded in the metadata row:
 *
 * - **A prototype with no versions is not an error.** It is the normal state of
 *   something created a minute ago, so instead of an empty slot or a warning it
 *   gets an invitation to upload, right where the version number will appear.
 * - **Zero open threads is good news and is rendered as such** — quiet muted
 *   text rather than a badge. A badge is a call to attention, and a loud "0"
 *   demands attention for the one state that needs none.
 */
export const PrototypeCard = ({
  prototype,
  onEdit,
  onDelete,
  onUpload,
}: PrototypeCardProps) => {
  const hasVersion = prototype.latestVersionNumber !== null;
  const openThreads = prototype.openThreadCount;

  const can = useCan();
  const owner = resourceOwnerId(prototype);
  /* Upload is "own prototype" for a Designer, any for Admin (spec matrix). */
  const canUpload = can.uploadVersion(owner);
  /* Edit/delete are owner-or-admin. */
  const canModify = can.modifyOwned(owner);
  const canDoAnything = canUpload || canModify;

  return (
    <Card className="relative flex flex-col shadow-none transition-colors hover:border-foreground/20 focus-within:border-foreground/20">
      <CardHeader className="gap-2 pb-4">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-base">
            <Link
              to="/prototypes/$prototypeId"
              params={{ prototypeId: prototype.id }}
              className="rounded-sm after:absolute after:inset-0 after:rounded-xl"
            >
              {prototype.name}
            </Link>
          </CardTitle>

          {canDoAnything ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="relative z-10 -mr-2 -mt-1 size-8 shrink-0 text-muted-foreground hover:text-foreground"
                >
                  <MoreHorizontal aria-hidden="true" />
                  <span className="sr-only">Actions for {prototype.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                {canUpload ? (
                  <DropdownMenuItem
                    onSelect={() => {
                      onUpload(prototype);
                    }}
                  >
                    <Upload aria-hidden="true" />
                    Upload a version
                  </DropdownMenuItem>
                ) : null}
                {canUpload && canModify ? <DropdownMenuSeparator /> : null}
                {canModify ? (
                  <>
                    <DropdownMenuItem
                      onSelect={() => {
                        onEdit(prototype);
                      }}
                    >
                      <Pencil aria-hidden="true" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="text-destructive focus:text-destructive"
                      onSelect={() => {
                        onDelete(prototype);
                      }}
                    >
                      <Trash2 aria-hidden="true" />
                      Delete
                    </DropdownMenuItem>
                  </>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>

        <CardDescription className="line-clamp-2 min-h-[2.5rem]">
          {prototype.description ?? 'No description.'}
        </CardDescription>
      </CardHeader>

      <CardContent className="mt-auto space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          {hasVersion ? (
            <Badge variant="secondary" className="font-mono">
              v{prototype.latestVersionNumber}
            </Badge>
          ) : canUpload ? (
            <Button
              variant="outline"
              size="sm"
              className="relative z-10 h-7 gap-1.5 border-dashed px-2 text-xs font-normal"
              onClick={() => {
                onUpload(prototype);
              }}
            >
              <Upload aria-hidden="true" className="size-3.5" />
              Upload the first version
            </Button>
          ) : (
            <span className="text-xs text-muted-foreground">No versions yet</span>
          )}

          {openThreads > 0 ? (
            <Badge variant="outline" className="gap-1 font-normal">
              <MessageSquare aria-hidden="true" className="size-3" />
              {openThreads} open {openThreads === 1 ? 'thread' : 'threads'}
            </Badge>
          ) : null}

          {prototype.sourceTool === null ? null : (
            <span className="truncate text-xs text-muted-foreground">
              From {prototype.sourceTool}
            </span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
          {openThreads > 0 ? null : (
            <>
              <span>No open comments</span>
              <span aria-hidden="true">·</span>
            </>
          )}
          <time
            dateTime={prototype.updatedAt}
            title={formatAbsoluteTime(prototype.updatedAt)}
          >
            Updated {formatRelativeTime(prototype.updatedAt)}
          </time>
        </div>
      </CardContent>
    </Card>
  );
};
